import { useEffect, useState } from 'react';

/**
 * `useState` shim backed by localStorage. The current value is read
 * synchronously on first render so the SPA doesn't flash the initial
 * before the persisted preference settles in.
 *
 * Failure modes are silent — `tryStorage` returns `null` for the
 * incognito / locked-down storage case and the hook degrades to a
 * plain useState. Callers don't need to know.
 *
 * `key` is the literal localStorage key. Callers should namespace
 * theirs (e.g. `wf-coe-app:scenarios:sort-field`) so the value
 * survives co-existing apps on the same origin.
 */
function tryLocalStorage(): Storage | null {
  try {
    const probe = '__wf-coe-pref-probe__';
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return window.localStorage;
  } catch {
    return null;
  }
}

export function usePersistentState<T extends string>(
  key: string,
  defaultValue: T,
  validate: (raw: string) => raw is T,
): [T, (next: T) => void] {
  const [value, setValue] = useState<T>(() => {
    const store = tryLocalStorage();
    if (!store) return defaultValue;
    const raw = store.getItem(key);
    return raw && validate(raw) ? raw : defaultValue;
  });
  useEffect(() => {
    const store = tryLocalStorage();
    if (!store) return;
    try {
      store.setItem(key, value);
    } catch {
      /* quota / disabled — keep going in-memory only */
    }
  }, [key, value]);
  return [value, setValue];
}
